import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import { formatDateTime } from '@/lib/format';
import { Search, X } from 'lucide-react';

export default function Logs() {
  const { effectiveCompanyId, isSuperAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  // Wave 4.07 — the Team Presence panel deep-links here as
  // ``/logs?user_id=<id>&user_name=<name>`` (its "View Activity" action),
  // so the filter is pre-seeded from the URL and a removable chip shows it.
  const [filter, setFilter] = useState(() => ({
    entity_type: '', action: '', search: '', start: '', end: '', limit: 200,
    user_id: searchParams.get('user_id') || '',
  }));
  const userName = searchParams.get('user_name') || '';

  const load = async () => {
    const params = { limit: filter.limit };
    if (effectiveCompanyId) params.company_id = effectiveCompanyId;
    if (filter.entity_type) params.entity_type = filter.entity_type;
    if (filter.action) params.action = filter.action;
    if (filter.search) params.search = filter.search;
    if (filter.user_id) params.user_id = filter.user_id;
    if (filter.start) params.start = `${filter.start}T00:00:00+05:30`;
    if (filter.end) params.end = `${filter.end}T23:59:59+05:30`;
    const { data } = await api.get('/logs/', { params });
    setList(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.entity_type, filter.action, filter.start, filter.end, filter.user_id, effectiveCompanyId]);

  const clearUserFilter = () => {
    setFilter((f) => ({ ...f, user_id: '' }));
    const next = new URLSearchParams(searchParams);
    next.delete('user_id');
    next.delete('user_name');
    setSearchParams(next, { replace: true });
  };

  const setPreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    setFilter((f) => ({ ...f, start: fmt(start), end: fmt(end) }));
  };
  const clearDates = () => setFilter((f) => ({ ...f, start: '', end: '' }));

  return (
    <AppShell title="Activity Logs" subtitle={isSuperAdmin && !effectiveCompanyId ? 'System-wide events across all companies' : 'Events for the selected company'}>
      <div className="sticky-filter-bar flex flex-wrap items-end gap-2 mb-3 rounded-lg px-3" data-testid="logs-filter-bar">
        <div>
          <Label className="text-[11px] text-slate-500">Entity</Label>
          <Select value={filter.entity_type || 'all'} onValueChange={(v) => setFilter({ ...filter, entity_type: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-44 h-9" data-testid="logs-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              <SelectItem value="diamond_packet">Diamond Packet</SelectItem>
              <SelectItem value="diamond_classification">Diamond Classification</SelectItem>
              <SelectItem value="gold_rate">Gold Rate</SelectItem>
              <SelectItem value="gold_ledger">Gold Ledger</SelectItem>
              <SelectItem value="gold_purity">Gold Purity</SelectItem>
              <SelectItem value="order">Order</SelectItem>
              <SelectItem value="vendor">Vendor</SelectItem>
              <SelectItem value="goldsmith">Goldsmith</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="company">Company</SelectItem>
              <SelectItem value="auth">Auth</SelectItem>
              <SelectItem value="report">Report</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Action</Label>
          <Select value={filter.action || 'all'} onValueChange={(v) => setFilter({ ...filter, action: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-40 h-9" data-testid="logs-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="create">Create</SelectItem>
              <SelectItem value="update">Update</SelectItem>
              <SelectItem value="delete">Delete</SelectItem>
              <SelectItem value="receipt">Receipt</SelectItem>
              <SelectItem value="issue">Issue</SelectItem>
              <SelectItem value="stage_change">Stage Change</SelectItem>
              <SelectItem value="login">Login</SelectItem>
              <SelectItem value="export">Export</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Label className="text-[11px] text-slate-500">Search</Label>
          <Search size={12} className="absolute left-3 bottom-2.5 text-slate-400" />
          <Input className="pl-7 h-9 w-56" placeholder="User email / entity…" value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && load()} data-testid="logs-search-input" />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">From</Label>
          <DatePicker className="h-9 w-44" value={filter.start} onChange={(e) => setFilter({ ...filter, start: e.target.value })} data-testid="logs-from-date" />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">To</Label>
          <DatePicker className="h-9 w-44" value={filter.end} onChange={(e) => setFilter({ ...filter, end: e.target.value })} data-testid="logs-to-date" />
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" className="h-9 text-[11px]" onClick={() => setPreset(7)} data-testid="logs-preset-7d">7d</Button>
          <Button variant="outline" size="sm" className="h-9 text-[11px]" onClick={() => setPreset(30)} data-testid="logs-preset-30d">30d</Button>
          <Button variant="outline" size="sm" className="h-9 text-[11px]" onClick={() => setPreset(90)} data-testid="logs-preset-90d">90d</Button>
          {(filter.start || filter.end) && (
            <Button variant="ghost" size="sm" className="h-9 text-[11px] text-slate-500" onClick={clearDates} data-testid="logs-clear-dates">Clear</Button>
          )}
        </div>
        <Button variant="outline" onClick={load} className="h-9" data-testid="logs-apply-button">Apply</Button>
      </div>

      {filter.user_id && (
        <div className="mb-3" data-testid="logs-user-filter-chip">
          <Badge variant="secondary" className="inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1.5 text-[12px] font-medium">
            Showing activity for {userName || 'selected user'}
            <button
              type="button"
              onClick={clearUserFilter}
              className="inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-slate-300/60 transition-colors"
              aria-label="Clear user filter"
              data-testid="logs-user-filter-clear"
            >
              <X size={12} />
            </button>
          </Badge>
        </div>
      )}
      <Card className="overflow-hidden">
        <div className="ledger-table overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <th className="text-left">Timestamp</th>
              <th className="text-left">User</th>
              <th className="text-left">Action</th>
              <th className="text-left">Entity</th>
              <th className="text-left">Label</th>
              <th className="text-left">Details</th>
            </tr></thead>
            <tbody data-testid="logs-table-body">
              {list.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-500">No logs match the filters.</td></tr>}
              {list.map((l) => (
                <tr key={l.id} data-testid={`log-row-${l.id}`}>
                  <td className="font-mono text-xs text-slate-600 whitespace-nowrap">{formatDateTime(l.timestamp)}</td>
                  <td className="text-xs text-slate-700">{l.user_email}</td>
                  <td><Badge variant="secondary" className="text-[10px] uppercase">{l.action}</Badge></td>
                  <td className="text-xs font-mono text-slate-600">{l.entity_type}</td>
                  <td className="text-xs text-slate-800">{l.entity_label}</td>
                  <td className="text-xs text-slate-500 font-mono">{l.details ? JSON.stringify(l.details).slice(0, 120) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </AppShell>
  );
}
